/**
 * Branded splash for a shared tracking link.
 * Plays on every load, then docks the marks onto the real header.
 */
import React, { useEffect, useRef, useState } from 'react';
import './SharedIntro.css';

const PUBLIC = process.env.PUBLIC_URL || '';
const LOGO = `${PUBLIC}/jtg-logo.png`;
const MARK = `${PUBLIC}/livelink-mark.png`;
const AWARDS = [
  `${PUBLIC}/top-fleet-stamp.png`,
  `${PUBLIC}/top-fleet-employer.png`,
  `${PUBLIC}/best-managed.png`,
];
const T = {
  logoIn: 100,
  partnerAt: 800,
  partnerHold: 2000,
  soloHold: 300,
  slowAfter: 1060,
  maxWait: 10000,
  dock: 800,
  short: 400,
};

/** Glide a splash mark onto its header twin: center-to-center, uniform scale. */
function flyTo(el, target, ms) {
  if (!el) return;
  const to = target?.getBoundingClientRect();
  if (!to || !to.width) {
    el.style.transition = `opacity ${ms}ms var(--motion-ease)`;
    el.style.opacity = '0';
    return;
  }
  const from = el.getBoundingClientRect();
  const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
  const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
  const k = Math.min(to.width / from.width, to.height / from.height);
  el.style.transition = `transform ${ms}ms var(--motion-ease)`;
  el.style.transform = `translate(${dx}px, ${dy}px) scale(${k})`;
}

/**
 * Short mode is a black fade for reduced motion.
 * An API error leaves companyUrl empty, so JTG and the LiveLink mark still dock.
 */
function SharedIntro({ mode, companyUrl, dataReady, getTargets, onReveal, onDone }) {
  const logoRef = useRef(null);
  const shakeRef = useRef(null);
  const partnerRef = useRef(null);
  const markRef = useRef(null);
  const cb = useRef({});
  cb.current = { onReveal, onDone, getTargets };

  const [stage, setStage] = useState(mode === 'short' ? 'leaving' : 'black');
  const [slow, setSlow] = useState(false);
  const [partnerShown, setPartnerShown] = useState(false);
  const [partnerOk, setPartnerOk] = useState(null);
  const [minElapsed, setMinElapsed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (mode !== 'full') return undefined;
    const brand = setTimeout(() => setStage((s) => (s === 'black' ? 'brand' : s)), T.logoIn);
    const elapsed = setTimeout(() => setMinElapsed(true), T.partnerAt);
    const wait = setTimeout(() => setSlow(true), T.slowAfter);
    const giveUp = setTimeout(() => setTimedOut(true), T.maxWait);
    return () => [brand, elapsed, wait, giveUp].forEach(clearTimeout);
  }, [mode]);

  useEffect(() => {
    if (!companyUrl) return undefined;
    let cancelled = false;
    const img = new Image();
    img.onload = () => { if (!cancelled) setPartnerOk(true); };
    img.onerror = () => { if (!cancelled) setPartnerOk(false); };
    img.src = companyUrl;
    return () => { cancelled = true; };
  }, [companyUrl]);

  useEffect(() => {
    if (stage !== 'brand') return undefined;
    const ready = dataReady && (!companyUrl || partnerOk !== null);
    if (!timedOut && ready && partnerOk) {
      setPartnerShown(true);
      setStage('partner');
      return undefined;
    }
    if ((minElapsed && ready) || timedOut) {
      const id = setTimeout(() => setStage('docking'), T.soloHold);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [stage, dataReady, companyUrl, partnerOk, minElapsed, timedOut]);

  useEffect(() => {
    if (stage !== 'partner') return undefined;
    const id = setTimeout(() => setStage('docking'), T.partnerHold);
    return () => clearTimeout(id);
  }, [stage]);

  useEffect(() => {
    if (stage !== 'docking' && stage !== 'leaving') return undefined;
    cb.current.onReveal();
    if (stage === 'docking') {
      const t = cb.current.getTargets();
      flyTo(logoRef.current, t.logo, T.dock);
      flyTo(shakeRef.current, t.shake, T.dock);
      flyTo(partnerRef.current, t.partner, T.dock);
      flyTo(markRef.current, t.product, T.dock);
    }
    const id = setTimeout(() => cb.current.onDone(), stage === 'docking' ? T.dock : T.short);
    return () => clearTimeout(id);
  }, [stage]);

  return (
    <div className={`shared-intro shared-intro--${stage}`} aria-hidden="true">
      <div className="shared-intro__backdrop" />
      {mode === 'full' && stage !== 'black' && stage !== 'leaving' && (
        <>
          <div className="shared-intro__slot shared-intro__slot--mark">
            <img ref={markRef} className="shared-intro__mark intro-enter" src={MARK} alt="" />
          </div>
          <div className="shared-intro__stack">
            <div className="shared-intro__slot shared-intro__slot--logo">
              <img ref={logoRef} className="shared-intro__logo intro-enter" src={LOGO} alt="" />
            </div>
            <div className="shared-intro__slot shared-intro__slot--shake">
              {partnerShown && (
                <span ref={shakeRef} className="shared-intro__shake intro-enter"><i className="fas fa-handshake" /></span>
              )}
            </div>
            <div className="shared-intro__slot shared-intro__slot--partner">
              {partnerShown && (
                <img ref={partnerRef} className="shared-intro__partner intro-enter" style={{ animationDelay: '250ms' }} src={companyUrl} alt="" />
              )}
            </div>
            <div className={`shared-intro__wait${slow && stage === 'brand' ? ' is-visible' : ''}`} />
          </div>
          <footer className="shared-intro__awards">
            <p>Recognized by</p>
            <div className="shared-intro__award-row">
              {AWARDS.map((src) => <img key={src} src={src} alt="" />)}
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

export default SharedIntro;
